import { z } from 'zod';
import { idSchema } from './common.js';
import { filterSchema, sortSchema } from './filters.js';
/**
 * Board views (RELAY-1038): projects by Project Stage, deals by Deal Status, jobs by Job Status
 * and a shortlist's candidates by Shortlist Flow stage. Every board is the same two calls per
 * entity: `…:counts` (one GROUP BY stage, the column totals) and `…:search` with the filter
 * narrowed to one stage (a column's own cursor-paged list). No board ever asks for the whole
 * data set. Jobs had both since RELAY-953; this adds the other three.
 */
/** One column's total: how many records the filter matches in that stage. */
export const stageCountSchema = z.object({ stageId: idSchema, count: z.number().int() });
const q = z.string().trim().min(1).max(200).optional();
const paging = {
    sort: z.array(sortSchema).max(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
};
// ── Projects ───────────────────────────────────────────────────────────────────────────────
export const PROJECT_FIELDS = {
    id: 'reference',
    name: 'text',
    companyId: 'reference',
    stageId: 'reference',
    ownerId: 'user',
    description: 'text',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
/**
 * What `POST /projects:search|counts` filter on: the project's own fields plus two over its
 * responsibles (Settings → Projects functions), which are not columns of the project and so stay
 * out of `PROJECT_FIELDS` (templates and trigger conditions read that). `responsibleId` = someone
 * in ANY function; `involvedId` = the owner OR any responsible (the Projects list's "Mine").
 */
export const PROJECT_SEARCH_FIELDS = {
    ...PROJECT_FIELDS,
    responsibleId: 'user',
    involvedId: 'user',
};
export const PROJECT_SORTABLE = [
    'createdAt',
    'updatedAt',
    'name',
];
/**
 * `POST /projects:search`: `q` matches the name. `pipelineId` (TS-1749) lists only the projects
 * in that pipeline (an archived place is left out), and `stageId` then means the stage there;
 * without it the list is every project, with its stage in the default pipeline.
 */
export const projectSearchSchema = z.object({
    q,
    companyId: idSchema.optional(),
    pipelineId: idSchema.optional(),
    filter: filterSchema.optional(),
    ...paging,
});
export const projectCountsRequestSchema = projectSearchSchema.pick({
    q: true,
    companyId: true,
    pipelineId: true,
    filter: true,
});
// ── Deals ──────────────────────────────────────────────────────────────────────────────────
export const DEAL_FIELDS = {
    id: 'reference',
    projectId: 'reference',
    title: 'text',
    value: 'number',
    currency: 'single_select',
    billingModel: 'single_select',
    stageId: 'reference',
    expectedCloseDate: 'date',
    paidAt: 'datetime',
    invoiceRef: 'text',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
/**
 * What `POST /deals:search|counts` filter on: `DEAL_FIELDS` plus the deal's project's people, as
 * `PROJECT_SEARCH_FIELDS` has them — `responsibleId` (someone in ANY function of the project) and
 * `involvedId` (the project's owner OR any responsible; the Deals list's "Mine").
 */
export const DEAL_SEARCH_FIELDS = {
    ...DEAL_FIELDS,
    responsibleId: 'user',
    involvedId: 'user',
};
export const DEAL_SORTABLE = [
    'createdAt',
    'updatedAt',
    'title',
    'value',
    'expectedCloseDate',
    'paidAt',
];
/**
 * `POST /deals:search`: deals across projects; `q` matches the title. `pipelineId` lists only the
 * deals in that Deal Flow pipeline, `stageId` then meaning the status there (as for projects).
 */
export const dealSearchSchema = z.object({
    q,
    projectId: idSchema.optional(),
    pipelineId: idSchema.optional(),
    filter: filterSchema.optional(),
    ...paging,
});
export const dealCountsRequestSchema = dealSearchSchema.pick({
    q: true,
    projectId: true,
    pipelineId: true,
    filter: true,
});
// ── A shortlist's candidates ───────────────────────────────────────────────────────────────
export const SHORTLIST_MEMBER_FIELDS = {
    candidateId: 'reference',
    stageId: 'reference',
    visibleToClient: 'boolean',
    stageEnteredAt: 'datetime',
    createdAt: 'datetime',
};
export const SHORTLIST_MEMBER_SORTABLE = [
    'createdAt',
    'stageEnteredAt',
];
/** `POST /shortlists/{id}/candidates:search`: `q` matches the candidate's name or email. */
export const shortlistMemberSearchSchema = z.object({
    q,
    filter: filterSchema.optional(),
    ...paging,
});
export const shortlistMemberCountsRequestSchema = shortlistMemberSearchSchema.pick({
    q: true,
    filter: true,
});
//# sourceMappingURL=boards.js.map